// src/components/Dashboard.js
import React, { useMemo, useState, useEffect, useCallback, useRef } from "react";
import "./Dashboard.css";
import { BACKEND_URL } from "../config";
import CoachRulesModal from "./CoachRulesModal";

import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  AreaChart,
  Area,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
} from "recharts";

function useIsMobileDashboard(breakpoint = 809) {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth <= breakpoint : false
  );

  useEffect(() => {
    const onResize = () => {
      setIsMobile(window.innerWidth <= breakpoint);
    };

    onResize();
    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, [breakpoint]);

  return isMobile;
}

function MobileCarouselSection({ children, className = "", slideClassName = "" }) {
  const isMobile = useIsMobileDashboard();
  const trackRef = useRef(null);
  const items = React.Children.toArray(children).filter(Boolean);
  const [activeIndex, setActiveIndex] = useState(0);

  const getSlides = useCallback(() => {
    const el = trackRef.current;
    if (!el) return [];
    return Array.from(el.children);
  }, []);

  useEffect(() => {
    setActiveIndex(0);

    const el = trackRef.current;
    if (!el) return;

    if (!isMobile) {
      el.scrollLeft = 0;
      return;
    }

    const slides = getSlides();
    if (!slides.length) {
      el.scrollLeft = 0;
      return;
    }

    const first = slides[0];
    const left = first.offsetLeft - (el.clientWidth - first.offsetWidth) / 2;
    el.scrollLeft = Math.max(0, left);
  }, [items.length, isMobile, getSlides]);

  const syncIndex = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;

    const slides = getSlides();
    if (!slides.length) return;

    const viewportCenter = el.scrollLeft + el.clientWidth / 2;
    let bestIndex = 0;
    let bestDistance = Number.POSITIVE_INFINITY;

    slides.forEach((slide, index) => {
      const slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
      const distance = Math.abs(slideCenter - viewportCenter);

      if (distance < bestDistance) {
        bestDistance = distance;
        bestIndex = index;
      }
    });

    setActiveIndex(bestIndex);
  }, [getSlides]);

  const scrollToIndex = useCallback(
    (index) => {
      const el = trackRef.current;
      if (!el) return;

      const slides = getSlides();
      if (!slides.length) return;

      const next = clamp(index, 0, Math.max(items.length - 1, 0));
      const target = slides[next];
      if (!target) return;

      const left =
        target.offsetLeft - (el.clientWidth - target.offsetWidth) / 2;

      el.scrollTo({ left, behavior: "smooth" });
      setActiveIndex(next);
    },
    [getSlides, items.length]
  );

  if (!isMobile) {
    return <div className={className}>{children}</div>;
  }

  return (
    <div className={`dash-mobile-carousel-shell ${className}`.trim()}>
      <div
        ref={trackRef}
        className="dash-mobile-carousel-track"
        onScroll={syncIndex}
      >
        {items.map((child, index) => (
          <div
            key={index}
            className={`dash-mobile-carousel-slide ${slideClassName}`.trim()}
            onClick={() => {
              if (items.length <= 1) return;
              scrollToIndex(activeIndex >= items.length - 1 ? 0 : activeIndex + 1);
            }}
          >
            {child}
          </div>
        ))}
      </div>

      {items.length > 1 ? (
        <div
          className="dash-mobile-carousel-dots"
          role="tablist"
          aria-label="Carousel pages"
        >
          {items.map((_, index) => (
            <button
              key={index}
              type="button"
              className={`dash-mobile-carousel-dot ${index === activeIndex ? "is-active" : ""}`}
              aria-label={`Go to slide ${index + 1}`}
              aria-selected={index === activeIndex}
              onClick={(e) => {
                e.stopPropagation();
                scrollToIndex(index);
              }}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}


function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function fmtNum(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  return Number(n).toLocaleString();
}

function timeLabel(iso) {
  if (!iso) return "";
  if (String(iso).includes("T")) return String(iso).split("T")[1].slice(0, 5);
  return String(iso).slice(0, 5);
}

function opLabel(op) {
  if (!op) return "";
  if (op === "lt") return "<";
  if (op === "gt") return ">";
  if (op === "eq") return "=";
  return String(op);
}

function metricProgressPct(rule, actual) {
  if (!rule) return null;

  const op = String(rule.operator ?? rule.op ?? "").toLowerCase();
  const target = Number(rule.value ?? rule.target ?? rule.threshold);
  if (!Number.isFinite(target) || target <= 0) return null;

  const a = actual == null ? 0 : Number(actual);
  if (!Number.isFinite(a)) return null;

  const MIN_SLIVER = 4;
  const normalized = clamp((a / target) * 100, 0, 100);

  if (op === "gt") {
    return normalized === 0 ? MIN_SLIVER : Math.max(MIN_SLIVER, normalized);
  }

  if (op === "lt") {
    const base = normalized === 0 ? MIN_SLIVER : Math.max(MIN_SLIVER, normalized);
    return a > target ? 100 : base;
  }

  if (op === "eq") {
    return a === target ? 100 : MIN_SLIVER;
  }

  return null;
}

function metricProgressDirection(rule) {
  if (!rule) return "";
  const op = String(rule.operator ?? rule.op ?? "").toLowerCase();
  if (op === "lt") return "lt";
  if (op === "gt") return "gt";
  if (op === "eq") return "eq";
  return "";
}

function metricProgressText(rule, actual) {
  if (!rule) return "";
  const op = String(rule.operator ?? rule.op ?? "").toLowerCase();
  const target = Number(rule.value ?? rule.target ?? rule.threshold);
  const a = actual == null ? null : Number(actual);

  if (!Number.isFinite(target)) return "";

  if (op === "lt") {
    if (!Number.isFinite(a)) return `Stay under ${fmtNum(target)}`;
    const remaining = target - a;
    return remaining >= 0
      ? `${fmtNum(remaining)} below limit`
      : `${fmtNum(Math.abs(remaining))} over limit`;
  }

  if (op === "gt") {
    if (!Number.isFinite(a)) return `Reach ${fmtNum(target)}`;
    const remaining = target - a;
    return remaining <= 0
      ? `${fmtNum(Math.abs(remaining))} above goal`
      : `${fmtNum(remaining)} to goal`;
  }

  if (op === "eq") {
    if (!Number.isFinite(a)) return `Target: ${fmtNum(target)}`;
    return a === target ? "On target" : `Target: ${fmtNum(target)}`;
  }

  return "";
}

function getMetricBarVisual(rule, actual) {
  const pct = metricProgressPct(rule, actual);
  if (pct == null) return null;

  const op = String(rule?.operator ?? rule?.op ?? "").toLowerCase();
  const target = Number(rule?.value ?? rule?.target ?? rule?.threshold);
  const a = actual == null ? 0 : Number(actual);

  if (!Number.isFinite(target) || target <= 0 || !Number.isFinite(a)) {
    return { width: `${pct}%`, background: "var(--accent)" };
  }

  const ratio = clamp(a / target, 0, 1);

  let background = "var(--accent)";

  if (op === "gt") {
    if (a >= target) background = "#22c55e";
    else if (ratio >= 0.5) background = "#f59e0b";
    else background = "#ef4444";
  } else if (op === "lt") {
    if (a > target) background = "#ef4444";
    else if (ratio >= 0.5) background = "#f59e0b";
    else background = "#22c55e";
  } else if (op === "eq") {
    background = a === target ? "#22c55e" : "#f59e0b";
  }

  return {
    width: `${pct}%`,
    background,
  };
}

const METRIC_OPTIONS = [
  {
    group: "Wearables",
    items: [
      { value: "wearables.steps", label: "Steps" },
      { value: "wearables.calories_burned", label: "Wearable Calories Burned" },
      { value: "wearables.active_minutes", label: "Active Minutes" },
      { value: "wearables.sleep_minutes", label: "Sleep Minutes" },
      { value: "wearables.resting_heart_rate", label: "Resting Heart Rate" },
      { value: "oura.readiness_score", label: "Oura Readiness Score" },
      { value: "oura.sleep_score", label: "Oura Sleep Score" },
      { value: "oura.activity_score", label: "Oura Activity Score" },
    ],
  },
  {
    group: "Nutrition (App)",
    items: [
      { value: "app.calories_consumed", label: "Calories Consumed" },
      { value: "app.protein_g", label: "Protein (g)" },
      { value: "app.carbs_g", label: "Carbs (g)" },
      { value: "app.fats_g", label: "Fats (g)" },
      { value: "app.water_oz", label: "Water (oz)" },
    ],
  },
  {
    group: "Exercise (App)",
    items: [
      { value: "app.exercise_calories_burned", label: "Exercise Calories Burned" },
      { value: "app.exercise_minutes", label: "Exercise Minutes" },
    ],
  },
  {
    group: "Habits",
    items: [
      { value: "habits.completion_pct", label: "Habits Completed (%)" },
      { value: "habits.completed_count", label: "Habits Completed (count)" },
      { value: "habits.total_count", label: "Habits Scheduled (count)" },
    ],
  },
  { group: "Weight", items: [{ value: "app.weight", label: "Weight" }] },
  {
    group: "Withings",
    items: [
      { value: "withings.weight_lbs", label: "Weight (lbs)" },
      { value: "withings.fat_ratio_percent", label: "Body Fat %" },
      { value: "withings.fat_mass_lbs", label: "Fat Mass (lbs)" },
      { value: "withings.fat_free_mass_lbs", label: "Fat Free Mass (lbs)" },
      { value: "withings.muscle_mass_lbs", label: "Muscle Mass (lbs)" },
      { value: "withings.bone_mass_lbs", label: "Bone Mass (lbs)" },
      { value: "withings.systolic", label: "Systolic" },
      { value: "withings.diastolic", label: "Diastolic" },
      { value: "withings.heart_rate", label: "Heart Rate" },
      { value: "withings.hydration_percent", label: "Hydration %" },
    ],
  },
];

const TILE_DISPLAY_OPTIONS = [
  { value: "number", label: "Big number" },
  { value: "ring", label: "Circle progress" },
  { value: "sparkline", label: "Tiny trend (sparkline)" },
  { value: "miniBar", label: "Tiny bars" },
  { value: "delta", label: "Change (delta)" },
];

const PROGRESS_DISPLAY_OPTIONS = [
  { value: "ring", label: "Circle progress" },
  { value: "bar", label: "Rising bar" },
];

const isGoalProgressTile = (w) => w?.kind === "goal_progress";
const isHabitProgressTile = (w) => w?.kind === "habit_progress";
const isProgressTile = (w) => isGoalProgressTile(w) || isHabitProgressTile(w);

const BIG_DISPLAY_OPTIONS = [
  { value: "line", label: "Line chart" },
  { value: "area", label: "Area chart" },
  { value: "bar", label: "Bar chart" },
];

const TIMEFRAME_OPTIONS = [
  { value: 1, label: "1 day" },
  { value: 7, label: "7 days" },
  { value: 14, label: "14 days" },
  { value: 30, label: "30 days" },
  { value: 90, label: "90 days" },
  { value: 365, label: "1 year" },
];

const AGG_OPTIONS = [
  { value: "avg", label: "Average" },
  { value: "sum", label: "Sum" },
  { value: "min", label: "Min" },
  { value: "max", label: "Max" },
  { value: "latest", label: "Latest" },
];

const COMPARE_DAYS_OPTIONS = [
  { value: 3, label: "vs prior 3d avg" },
  { value: 7, label: "vs prior 7d avg" },
  { value: 14, label: "vs prior 14d avg" },
  { value: 30, label: "vs prior 30d avg" },
];

function metricLabel(metricKey) {
  for (const g of METRIC_OPTIONS) {
    const found = g.items.find((x) => x.value === metricKey);
    if (found) return found.label;
  }
  return metricKey;
}

function tileTitle(w) {
  if (!w) return "";
  if (isGoalProgressTile(w)) return w.label || "Goal progress";
  if (isHabitProgressTile(w)) return w.label || "Habit progress";
  return w.label || metricLabel(w.metric_key);
}

function compactDateLabel(ymd) {
  if (!ymd) return "";
  return String(ymd).slice(5);
}

function TooltipContent({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const v = payload[0]?.value;
  return (
    <div
      style={{
        background: "white",
        border: "1px solid rgba(0,0,0,0.12)",
        borderRadius: 10,
        padding: 10,
      }}
    >
      <div style={{ fontWeight: 800, marginBottom: 4 }}>{label}</div>
      <div style={{ color: "rgba(0,0,0,0.7)" }}>{v == null ? "—" : fmtNum(v)}</div>
    </div>
  );
}

function RingProgress({ value, max, label }) {
  const v = Number(value);
  const m = Number(max);
  const pct = !m || !Number.isFinite(v) || !Number.isFinite(m) ? 0 : clamp((v / m) * 100, 0, 999);
  const pctDisplay = m ? Math.round(pct) : 0;
  const bg = `conic-gradient(currentColor ${pct}%, rgba(0,0,0,0.08) 0)`;

  return (
    <div className="dash-ring-wrap" aria-label={label || "progress"}>
      <div className="dash-ring" style={{ background: bg }}>
        <div className="dash-ring-inner">
          <div className="dash-ring-num">{m ? `${pctDisplay}%` : "—"}</div>
        </div>
      </div>
    </div>
  );
}

function safeArray(x) {
  return Array.isArray(x) ? x : [];
}

function calcDeltaFromPoints(points, compareDays) {
  const vals = (points || [])
    .map((p) => (p?.value == null ? null : Number(p.value)))
    .filter((v) => Number.isFinite(v));

  if (!vals.length) return { latest: null, base: null, pct: null, dir: 0 };

  const latest = vals[vals.length - 1];

  const n = Math.max(1, Number(compareDays || 7));
  const baseSlice = vals.slice(Math.max(0, vals.length - 1 - n), Math.max(0, vals.length - 1));
  const baseVals = baseSlice.filter((v) => Number.isFinite(v));

  if (!baseVals.length) return { latest, base: null, pct: null, dir: 0 };

  const base = baseVals.reduce((a, b) => a + b, 0) / baseVals.length;

  if (!Number.isFinite(base) || base === 0)
    return { latest, base, pct: null, dir: latest > base ? 1 : latest < base ? -1 : 0 };

  const pct = ((latest - base) / Math.abs(base)) * 100;
  const dir = pct > 0 ? 1 : pct < 0 ? -1 : 0;

  return { latest, base, pct, dir };
}

async function fetchJSON(url, opts = {}) {
  const res = await fetch(url, opts);
  const text = await res.text();
  let json = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    json = null;
  }
  if (!res.ok) {
    const err = new Error(text || `HTTP ${res.status}`);
    err.status = res.status;
    err.body = json;
    throw err;
  }
  return json;
}

function toISODate(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function addDaysISO(iso, delta) {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + delta);
  return toISODate(d);
}

function dayLabel(iso) {
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString(undefined, { weekday: "short" });
}

function dayNum(iso) {
  return String(iso).slice(8, 10);
}

function stableStringify(obj) {
  const seen = new WeakSet();
  const sorter = (x) => {
    if (x === null || typeof x !== "object") return x;
    if (seen.has(x)) return null;
    seen.add(x);
    if (Array.isArray(x)) return x.map(sorter);
    const out = {};
    Object.keys(x)
      .sort()
      .forEach((k) => {
        out[k] = sorter(x[k]);
      });
    return out;
  };
  return JSON.stringify(sorter(obj));
}

function isBlank(x) {
  if (x == null) return true;
  if (typeof x === "string") return x.trim() === "";
  return false;
}

function isAnswered(v) {
  if (v == null) return false;
  if (Array.isArray(v)) return v.filter((x) => x !== null && x !== undefined && String(x).trim() !== "").length > 0;
  if (typeof v === "boolean") return true;
  if (typeof v === "number") return Number.isFinite(v);
  if (typeof v === "string") return v.trim() !== "";
  if (typeof v === "object") {
    const name = v.name || v.filename || v.file_name || v.originalname;
    return name ? String(name).trim() !== "" : false;
  }
  return false;
}

function isQuestionnaireComplete(q) {
  if (!q || typeof q !== "object") return false;

  const requiredKeys = [
    "steps_range",
    "current_exercise",
    "gym_access",
    "gym_experience",
    "workouts_love",
    "workouts_days_per_week",
    "health_conditions",
    "current_injuries",
    "past_injuries",
    "pain_notes",
    "active_hobbies",
    "eating_habits",
    "foods_love",
    "foods_hate_allergies",
    "fruits_veggies",
    "supplements",
    "yo_yo_diet",
    "tracked_macros_before",
    "smart_watch",
    "activity_level_label",
    "preferred_workout_days",
  ];

  for (const k of requiredKeys) {
    if (!Object.prototype.hasOwnProperty.call(q, k)) return false;
    if (!isAnswered(q[k])) return false;
  }

  return true;
}

function isCoachRulesEmpty(rules) {
  const r = rules && typeof rules === "object" ? rules : {};

  const listFields = [
    "dietary_restrictions",
    "allergies",
    "dislikes",
    "medical_constraints",
    "injury_constraints",
    "forbidden_topics",
  ];

  for (const k of listFields) {
    const v = r[k];
    if (Array.isArray(v) && v.filter(Boolean).length > 0) return false;
  }

  if (!isBlank(r.notes_for_coach)) return false;

  const tone = String(r.tone || "").trim().toLowerCase();
  if (tone && tone !== "encouraging") return false;

  return true;
}

async function fetchFirstOk(urls, opts) {
  let lastErr = null;
  for (const url of urls) {
    try {
      const res = await fetch(url, opts);
      if (!res.ok) {
        lastErr = new Error(`HTTP ${res.status}`);
        continue;
      }
      const text = await res.text();
      try {
        return text ? JSON.parse(text) : null;
      } catch {
        return null;
      }
    } catch (e) {
      lastErr = e;
    }
  }
  if (lastErr) throw lastErr;
  return null;
}

function normalizeMaybeDate(value) {
  if (!value) return "";
  const s = String(value).trim();
  if (!s) return "";
  const m = s.match(/\d{4}-\d{2}-\d{2}/);
  return m ? m[0] : "";
}

function collectGoalDueItemsForDate(source, isoDate) {
  const goals = Array.isArray(source) ? source : [];
  const items = [];

  for (const goal of goals) {
    const goalId = goal?.goal_id ?? goal?.id ?? goal?._id;
    const goalTitle = goal?.title ?? goal?.name ?? "Goal";
    const goalDescription = goal?.description ?? goal?.notes ?? "";
    const goalDue =
      normalizeMaybeDate(goal?.due_date) ||
      normalizeMaybeDate(goal?.due_at) ||
      normalizeMaybeDate(goal?.target_date) ||
      normalizeMaybeDate(goal?.deadline) ||
      normalizeMaybeDate(goal?.end_date);

    if (goalId && goalDue === isoDate && !goal?.completed) {
      items.push({
        kind: "goal",
        goal_id: goalId,
        title: goalTitle,
        description: goalDescription,
        due_date: goalDue,
        is_milestone: false,
      });
    }

    const milestoneLists = [
      goal?.milestones,
      goal?.goal_milestones,
      goal?.milestone_items,
      goal?.progress_milestones,
      goal?.sub_goals,
    ].filter(Array.isArray);

    for (const milestoneList of milestoneLists) {
      for (const milestone of milestoneList) {
        const milestoneDue =
          normalizeMaybeDate(milestone?.due_date) ||
          normalizeMaybeDate(milestone?.due_at) ||
          normalizeMaybeDate(milestone?.target_date) ||
          normalizeMaybeDate(milestone?.deadline) ||
          normalizeMaybeDate(milestone?.end_date);
        if (milestoneDue !== isoDate) continue;
        items.push({
          kind: "goal_milestone",
          goal_id: goalId || milestone?.goal_id || null,
          milestone_id: milestone?.milestone_id ?? milestone?.id ?? milestone?._id ?? null,
          title: `${goalTitle}: ${milestone?.title ?? milestone?.name ?? milestone?.label ?? "Milestone"}`,
          description: milestone?.description ?? milestone?.notes ?? goalDescription,
          due_date: milestoneDue,
          is_milestone: true,
        });
      }
    }
  }

  return items;
}

function Dashboard({ dashboardData, onNavigate, userId, token, onOpenLogChoice, onStartWorkout, showToast }) {
  const [selectedDate, setSelectedDate] = useState(() => {
    const initial = dashboardData?.date;
    return initial && /^\d{4}-\d{2}-\d{2}$/.test(initial) ? initial : toISODate(new Date());
  });

  const [dashData, setDashData] = useState(dashboardData || null);
  const [dashLoading, setDashLoading] = useState(false);
  const didSyncInitialDate = useRef(false);

  const [questionnaireFilled, setQuestionnaireFilled] = useState(true);
  const [showCoachRules, setShowCoachRules] = useState(false);

  const tileRefs = useRef({});
  const bigRefs = useRef({});
  const [pendingScroll, setPendingScroll] = useState(null);
  const [todayHabitFeed, setTodayHabitFeed] = useState([]);
  const [todayHabitFeedLoading, setTodayHabitFeedLoading] = useState(false);

  const [coachProgramSummary, setCoachProgramSummary] = useState(null);
  const [coachProgramSummaryLoading, setCoachProgramSummaryLoading] = useState(false);

  useEffect(() => {
    if (!pendingScroll?.id) return;
    const map = pendingScroll.type === "big" ? bigRefs.current : tileRefs.current;
    const el = map[pendingScroll.id];
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    setPendingScroll(null);
  }, [pendingScroll]);

  useEffect(() => {
    if (!dashboardData) return;
    setDashData(dashboardData);
    if (!didSyncInitialDate.current && dashboardData?.date && /^\d{4}-\d{2}-\d{2}$/.test(dashboardData.date)) {
      setSelectedDate(dashboardData.date);
      didSyncInitialDate.current = true;
    }
  }, [dashboardData]);

  const loadTodayHabitsFeed = useCallback(
    async (isoDate) => {
      if (!token || !isoDate) return;
      setTodayHabitFeedLoading(true);
      try {
        const res = await fetch(
          `${BACKEND_URL}/api/habits/today?date=${encodeURIComponent(isoDate)}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            cache: "no-store",
          }
        );
        if (!res.ok) throw new Error(await res.text());
        const out = await res.json();
        setTodayHabitFeed(Array.isArray(out?.habits) ? out.habits : []);
      } catch (e) {
        console.warn("Failed to load today habits feed:", e?.message || e);
        setTodayHabitFeed([]);
      } finally {
        setTodayHabitFeedLoading(false);
      }
    },
    [token]
  );

  const loadCoachProgramSummary = useCallback(async () => {
    if (!token) return;
    setCoachProgramSummaryLoading(true);
    try {
      const candidates = [
        `${BACKEND_URL}/api/coach/programs/active/summary`,
        `${BACKEND_URL}/api/coach/programs/summary/active`,
        `${BACKEND_URL}/api/coach/programs/active`,
      ];

      const data = await fetchFirstOk(
        candidates,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setCoachProgramSummary(data?.summary || data?.program || data || null);
    } catch (e) {
      console.warn("Failed to load coach program summary:", e?.message || e);
      setCoachProgramSummary(null);
    } finally {
      setCoachProgramSummaryLoading(false);
    }
  }, [token]);

  const loadDashboardForDate = useCallback(
    async (isoDate) => {
      if (!token || !userId || !isoDate) return;
      setDashLoading(true);
      try {
        const url = `${BACKEND_URL}/api/dashboard/${userId}?date=${encodeURIComponent(isoDate)}`;
        const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` }, cache: "no-store" });
        if (!res.ok) throw new Error(await res.text());
        const out = await res.json();
        setDashData(out);
      } catch (e) {
        console.warn("Failed to load dashboard for date:", isoDate, e?.message || e);
      } finally {
        setDashLoading(false);
      }
    },
    [token, userId]
  );

  const [allGoals, setAllGoals] = useState([]);
  const [allGoalsLoading, setAllGoalsLoading] = useState(false);

  const loadAllGoals = useCallback(async () => {
    if (!token) return;
    setAllGoalsLoading(true);

    try {
      const opts = { headers: { Authorization: `Bearer ${token}` } };

      const tryJson = async (url) => {
        const res = await fetch(url, opts);
        if (!res.ok) return null;
        const txt = await res.text();
        try {
          return txt ? JSON.parse(txt) : null;
        } catch {
          return null;
        }
      };

      const candidates = [
        userId ? `${BACKEND_URL}/api/goals/list?user_id=${encodeURIComponent(userId)}` : null,
        `${BACKEND_URL}/api/goals/list?user_id=me`,
        `${BACKEND_URL}/api/goals/list`,
        `${BACKEND_URL}/api/goals`,
      ].filter(Boolean);

      let data = null;
      for (const url of candidates) {
        data = await tryJson(url);
        if (data) break;
      }

      const raw =
        Array.isArray(data?.goals) ? data.goals :
        Array.isArray(data) ? data :
        Array.isArray(data?.items) ? data.items :
        [];

      const normalized = raw
        .map((g) => ({
          goal_id: g?.goal_id ?? g?.id ?? g?._id,
          title: g?.title ?? g?.name ?? "Goal",
          description: g?.description ?? g?.notes ?? "",
          completed: !!g?.completed,
          due_date:
            normalizeMaybeDate(g?.due_date) ||
            normalizeMaybeDate(g?.due_at) ||
            normalizeMaybeDate(g?.target_date) ||
            normalizeMaybeDate(g?.deadline) ||
            normalizeMaybeDate(g?.end_date),
          milestones:
            (Array.isArray(g?.milestones) && g.milestones) ||
            (Array.isArray(g?.goal_milestones) && g.goal_milestones) ||
            (Array.isArray(g?.milestone_items) && g.milestone_items) ||
            (Array.isArray(g?.progress_milestones) && g.progress_milestones) ||
            (Array.isArray(g?.sub_goals) && g.sub_goals) ||
            [],
          metric_rule: g?.metric_rule || null,
          metric_actual: g?.metric_actual ?? null,
          metric_met: g?.metric_met ?? null,
          total_milestones: g?.total_milestones ?? null,
          done_milestones: g?.done_milestones ?? null,
          pct: g?.pct ?? g?.percent ?? null,
        }))
        .filter((g) => g.goal_id)
        .filter((g) => !g.completed);

      setAllGoals(normalized);
    } catch (e) {
      console.warn("Failed to load goals list:", e?.message || e);
      setAllGoals([]);
    } finally {
      setAllGoalsLoading(false);
    }
  }, [token, userId]);

  useEffect(() => {
    if (!selectedDate) return;
    loadTodayHabitsFeed(selectedDate);
  }, [selectedDate, loadTodayHabitsFeed]);

  useEffect(() => {
    loadCoachProgramSummary();
  }, [loadCoachProgramSummary]);

  useEffect(() => {
    loadAllGoals();
  }, [loadAllGoals]);

  const [allHabits, setAllHabits] = useState([]);
  const [allHabitsLoading, setAllHabitsLoading] = useState(false);

  const loadAllHabits = useCallback(async () => {
    if (!token) return;
    setAllHabitsLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/habits?user_id=me`, { headers: { Authorization: `Bearer ${token}` }, cache: "no-store" });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setAllHabits(Array.isArray(data?.habits) ? data.habits : []);
    } catch (e) {
      console.warn("Failed to load habits list:", e?.message || e);
      setAllHabits([]);
    } finally {
      setAllHabitsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadAllHabits();
  }, [loadAllHabits]);

  const HALF = 3;
  const WINDOW = HALF * 2 + 1;

  const daySquares = useMemo(() => {
    return Array.from({ length: WINDOW }).map((_, i) => addDaysISO(selectedDate, i - HALF));
  }, [selectedDate, WINDOW, HALF]);

  const onPickDay = useCallback(
    (iso) => {
      setSelectedDate(iso);
      loadDashboardForDate(iso);
    },
    [loadDashboardForDate]
  );

  const pageDays = useCallback(
    (deltaDays) => {
      const next = addDaysISO(selectedDate, deltaDays);
      onPickDay(next);
    },
    [selectedDate, onPickDay]
  );

  const todayISO = toISODate(new Date());

  const goToToday = useCallback(() => {
    onPickDay(todayISO);
  }, [onPickDay, todayISO]);

  const [showMobileLogMenu, setShowMobileLogMenu] = useState(false);

  const openFoodLog = useCallback(() => {
    setShowMobileLogMenu(false);
    onNavigate?.("food");
    if (typeof onOpenLogChoice === "function") setTimeout(() => onOpenLogChoice("food"), 0);
  }, [onNavigate, onOpenLogChoice]);

  const openExerciseLog = useCallback(() => {
    setShowMobileLogMenu(false);
    onNavigate?.("exercise");
    if (typeof onOpenLogChoice === "function") setTimeout(() => onOpenLogChoice("exercise"), 0);
  }, [onNavigate, onOpenLogChoice]);

  const loadSetupStatus = useCallback(async () => {
    if (!token) return;

    try {
      const opts = { headers: { Authorization: `Bearer ${token}` } };

      const profile = await fetchFirstOk(
        [
          userId ? `${BACKEND_URL}/api/users/${encodeURIComponent(userId)}/profile` : null,
          `${BACKEND_URL}/api/users/me/profile`,
          `${BACKEND_URL}/api/users/me`,
          `${BACKEND_URL}/api/users/profile`,
          `${BACKEND_URL}/api/profile/me`,
          `${BACKEND_URL}/api/profile`,
        ].filter(Boolean),
        opts
      );

      const q =
        (profile?.questionnaire && typeof profile.questionnaire === "object")
          ? profile.questionnaire
          : (profile?.profile?.questionnaire && typeof profile.profile.questionnaire === "object")
          ? profile.profile.questionnaire
          : (profile?.user?.questionnaire && typeof profile.user.questionnaire === "object")
          ? profile.user.questionnaire
          : null;

      if (q) {
        const mergedQ = { ...q };
        if (!Object.prototype.hasOwnProperty.call(mergedQ, "activity_level_label") && profile?.activity_level_label != null) {
          mergedQ.activity_level_label = profile.activity_level_label;
        }
        if (!Object.prototype.hasOwnProperty.call(mergedQ, "preferred_workout_days") && profile?.preferred_workout_days != null) {
          mergedQ.preferred_workout_days = profile.preferred_workout_days;
        }
        setQuestionnaireFilled(isQuestionnaireComplete(mergedQ));
      } else {
        setQuestionnaireFilled(true);
      }
    } catch (e) {
      console.warn("Failed to load questionnaire status:", e?.message || e);
      setQuestionnaireFilled(true);
    }

      }, [token, userId]);

  useEffect(() => {
    loadSetupStatus();
  }, [loadSetupStatus]);

  const data = dashData;

  const dailyTarget = Number(data?.daily_target || 0);
  const consumed = Number(data?.calories_consumed || 0);
  const burned = Number(data?.calories_burned || 0);
  const net = Number(data?.net_calories ?? consumed - burned);
  const remaining = Number(data?.remaining_calories ?? dailyTarget - net);

  const progressPct = useMemo(() => {
    if (!dailyTarget || dailyTarget <= 0) return 0;
    return clamp((net / dailyTarget) * 100, 0, 140);
  }, [net, dailyTarget]);

  const wearable = data?.wearable || {};
  const sourceLabel = wearable?.source ? String(wearable.source).replace("_", " ") : null;
  const remainingColor =
    remaining < 0 ? "var(--dash-danger)" : remaining === 0 ? "var(--dash-warn)" : "var(--dash-good)";

  const todayHabits = useMemo(() => {
    const dashboardHabits = safeArray(data?.today?.habits);
    const feedHabits = safeArray(todayHabitFeed);

    const byId = new Map();

    for (const h of dashboardHabits) {
      if (!h?.habit_id) continue;
      byId.set(h.habit_id, h);
    }

    for (const h of feedHabits) {
      if (!h?.habit_id) continue;
      const existing = byId.get(h.habit_id) || {};
      byId.set(h.habit_id, {
        ...existing,
        ...h,
        completed:
          h.completed ??
          existing.completed ??
          false,
      });
    }

    return Array.from(byId.values());
  }, [data?.today?.habits, todayHabitFeed]);

  const todayGoalsDue = useMemo(() => {
    const direct = Array.isArray(data?.today?.goals_due) ? data.today.goals_due : [];
    const fallback = collectGoalDueItemsForDate(allGoals, selectedDate).filter((item) => !item.is_milestone);
    const merged = [...direct, ...fallback];
    const seen = new Set();
    return merged.filter((item) => {
      const key = item?.goal_id || item?.id || item?.title;
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [data?.today?.goals_due, allGoals, selectedDate]);

  const todayGoalMilestonesDue = useMemo(() => {
    const direct =
      (Array.isArray(data?.today?.goal_milestones_due) && data.today.goal_milestones_due) ||
      (Array.isArray(data?.today?.milestones_due) && data.today.milestones_due) ||
      [];
    const fallback = collectGoalDueItemsForDate(allGoals, selectedDate).filter((item) => item.is_milestone);
    const merged = [...direct, ...fallback];
    const seen = new Set();
    return merged.filter((item) => {
      const key = item?.milestone_id || `${item?.goal_id || "goal"}:${item?.title || "milestone"}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [data?.today?.goal_milestones_due, data?.today?.milestones_due, allGoals, selectedDate]);
  const todayEvents = useMemo(() => data?.today?.events ?? [], [data?.today?.events]);
  const todayWorkouts = useMemo(() => data?.today?.workouts ?? [], [data?.today?.workouts]);

  const [programCount, setProgramCount] = useState(null);
  const [programsLoading, setProgramsLoading] = useState(false);

  const standaloneEvents = useMemo(() => {
    const workoutEventIds = new Set(safeArray(todayWorkouts).map((w) => w?.event_id).filter(Boolean));
    return safeArray(todayEvents).filter((e) => {
      if (!e || e?.habit_id || e?.goal_id) return false;
      if (workoutEventIds.has(e.event_id)) return false;

      const cat = String(e.category || "").toLowerCase();
      const meta = e?.meta || {};
      const looksLikeWorkout =
        cat === "workout" ||
        cat === "training" ||
        ((Array.isArray(e?.sub_items) && e.sub_items.length > 0)) ||
        Boolean(meta?.session_id || meta?.workout_id || meta?.program_id || meta?.template_type);

      return !looksLikeWorkout;
    });
  }, [todayEvents, todayWorkouts]);

  const hasAnyHabitsGoalsEvents = useMemo(() => {
    return (
      safeArray(todayHabits).length > 0 ||
      safeArray(todayGoalsDue).length > 0 ||
      safeArray(todayGoalMilestonesDue).length > 0 ||
      standaloneEvents.length > 0
    );
  }, [todayHabits, todayGoalsDue, todayGoalMilestonesDue, standaloneEvents]);

  const shouldShowProgramSetup = programCount === 0;

  const loadProgramCount = useCallback(async () => {
    if (!token) return;
    setProgramsLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/coach/programs`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(await res.text());
      const out = await res.json();
      const list = Array.isArray(out?.programs) ? out.programs : [];
      setProgramCount(list.length);
    } catch (e) {
      console.warn("Failed to load program count:", e?.message || e);
      setProgramCount(null);
    } finally {
      setProgramsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadProgramCount();
  }, [loadProgramCount]);

  const openCreateProgramFromDashboard = useCallback(() => {
    onNavigate?.("coach_programs");
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent("coach:openCreateProgram", { detail: { source: "dashboard" } }));
    }, 150);
  }, [onNavigate]);

  const [habitOverride, setHabitOverride] = useState({});
  const [savingHabitId, setSavingHabitId] = useState(null);

  useEffect(() => {
    const next = {};
    for (const h of todayHabits) {
      if (h?.habit_id && h.habit_type === "checkbox") next[h.habit_id] = !!h.completed;
    }
    setHabitOverride(next);
  }, [data?.date, todayHabits]);

  const toggleHabitComplete = useCallback(
    async (habitId, checked) => {
      if (!token || !habitId) return;
      if (!data?.date) return;

      setHabitOverride((prev) => ({ ...prev, [habitId]: checked }));
      setSavingHabitId(habitId);

      try {
        const res = await fetch(`${BACKEND_URL}/api/habits/${habitId}/complete`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ habit_id: habitId, date: data.date, checked: !!checked }),
        });
        if (!res.ok) throw new Error(await res.text());
      } catch (e) {
        const original = !!todayHabits.find((h) => h?.habit_id === habitId)?.completed;
        setHabitOverride((prev) => ({ ...prev, [habitId]: original }));
        showToast?.("Error updating habit: " + (e?.message || String(e)), "error");
      } finally {
        setSavingHabitId(null);
      }
    },
    [token, data?.date, todayHabits, showToast]
  );

  const [waterOz, setWaterOz] = useState(16);
  const [waterSaving, setWaterSaving] = useState(false);

  const logWater = useCallback(async () => {
    if (!token || !userId) return;
    if (!data?.date) return;

    const oz = Number(waterOz);
    if (!Number.isFinite(oz) || oz <= 0) {
      showToast?.("Enter a valid ounces amount.", "error");
      return;
    }

    setWaterSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/water/log`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ user_id: userId, ounces: oz, date: data.date }),
      });
      if (!res.ok) throw new Error(await res.text());
    } catch (e) {
      showToast?.("Error logging water: " + (e?.message || String(e)), "error");
    } finally {
      setWaterSaving(false);
    }
  }, [token, userId, data?.date, waterOz, showToast]);

  const todoItems = useMemo(() => {
    const items = [];

    if (questionnaireFilled === false) {
      items.push({
        kind: "questionnaire_setup",
        id: "fill_questionnaire",
        title: "Fill out questionnaire",
        time: "09:00",
        done: false,
        subtitle: "Improve suggestions by adding preferences, injuries, and routine.",
      });
    }


    if (shouldShowProgramSetup) {
      items.push({
        kind: "program_setup",
        id: "create_program",
        title: "Create a Lifestyle Program",
        time: "09:00",
        done: false,
        subtitle: "Generate a program to auto-create habits, events, and suggested dashboard widgets.",
      });
    }

    for (const h of todayHabits) {
      if (!h?.habit_id) continue;

      const isMetric = h.habit_type === "metric";
      const doneCheckbox = h.habit_type === "checkbox" ? (habitOverride[h.habit_id] ?? !!h.completed) : false;
      const doneMetric = isMetric ? !!h.metric_met : false;

      let subtitle = h.description ? String(h.description) : "Habit";
      if (isMetric && h.metric_rule) {
        const r = h.metric_rule;
        const actual = h.metric_actual;
        const metricLine = `Metric: ${r.domain}.${r.field} ${opLabel(r.operator)} ${r.value}${
          actual !== null && actual !== undefined ? ` (actual: ${actual})` : ""
        }`;
        subtitle = h.description ? `${h.description} • ${metricLine}` : metricLine;
      }

      items.push({
        kind: "habit",
        id: h.habit_id,
        habit_type: h.habit_type,
        title: h.name || "Habit",
        time: h.time_of_day || "09:00",
        done: isMetric ? doneMetric : doneCheckbox,
        metric_met: isMetric ? !!h.metric_met : null,
        metric_actual: isMetric ? (h.metric_actual ?? null) : null,
        metric_rule: isMetric ? (h.metric_rule ?? null) : null,
        subtitle,
      });
    }

    for (const g of todayGoalsDue) {
      if (!g?.goal_id) continue;
      items.push({
        kind: "goal",
        id: g.goal_id,
        title: g.title || "Goal",
        time: "09:00",
        done: false,
        subtitle: g.description ? String(g.description) : "Goal due today",
      });
    }

    for (const m of todayGoalMilestonesDue) {
      items.push({
        kind: "goal_milestone",
        id: m?.milestone_id || `${m?.goal_id || "goal"}:${m?.title || "milestone"}`,
        title: m?.title || "Goal milestone",
        time: "09:00",
        done: false,
        subtitle: m?.description ? String(m.description) : "Goal milestone due today",
      });
    }

    const eventById = new Map(safeArray(todayEvents).map((e) => [e.event_id, e]));

    for (const w of safeArray(todayWorkouts)) {
      if (!w?.event_id) continue;

      const ev = eventById.get(w.event_id) || null;

      const status = String(w.status || w.session_status || w.state || "").toLowerCase();
      const started = Boolean(
        w.started_at ||
        w?.meta?.started_at ||
        w?.meta?.has_progress === true ||
        status === "in_progress" || status === "started"
      );

      const finished = Boolean(
        w.completed || w?.meta?.completed ||
        w.ended_at || w.finished_at || w.completed_at ||
        ev?.completed || ev?.meta?.completed ||
        ev?.completed_at || ev?.ended_at ||
        status === "completed" || status === "finished" || status === "done"
      );

      items.push({
        kind: "workout",
        id: w.event_id,
        title: w.title || ev?.title || "Workout",
        time: timeLabel(w.start_at || ev?.start_at) || "09:00",
        done: finished,
        started,
        finished,
        subtitle: Array.isArray(w.sub_items) && w.sub_items.length
          ? `${w.sub_items.length} exercises`
          : Array.isArray(ev?.sub_items) && ev.sub_items.length
          ? `${ev.sub_items.length} exercises`
          : "Workout scheduled",
      });
    }

    const seenHabitIds = new Set(items.filter((x) => x.kind === "habit").map((x) => x.id));
    for (const e of todayEvents) {
      if (!e?.event_id || !e?.habit_id) continue;
      if (seenHabitIds.has(e.habit_id)) continue;
      items.push({
        kind: "habit",
        id: e.habit_id,
        habit_type: "checkbox",
        title: e.title || "Habit",
        time: timeLabel(e.start_at) || "09:00",
        done: Boolean(e.completed || e?.meta?.completed),
        subtitle: e.description ? String(e.description) : "Habit",
      });
      seenHabitIds.add(e.habit_id);
    }

    const workoutEventIds = new Set(safeArray(todayWorkouts).map((w) => w?.event_id).filter(Boolean));
    for (const e of todayEvents) {
      if (!e?.event_id) continue;
      if (e.habit_id || e.goal_id) continue;
      if (workoutEventIds.has(e.event_id)) continue;

      const cat = String(e.category || "").toLowerCase();
      const meta = e?.meta || {};
      const looksLikeWorkout =
        cat === "workout" ||
        cat === "training" ||
        ((Array.isArray(e?.sub_items) && e.sub_items.length > 0)) ||
        Boolean(meta?.session_id || meta?.workout_id || meta?.program_id || meta?.template_type);
      if (looksLikeWorkout) continue;

      items.push({
        kind: "event",
        id: e.event_id,
        title: e.title || "Event",
        time: timeLabel(e.start_at),
        done: false,
        subtitle: e.description
          ? String(e.description)
          : e.location
          ? `Event • ${e.location}`
          : e.category
          ? `Event • ${e.category}`
          : "Event",
      });
    }

    const todoPriority = {
      workout: 0,
      habit: 1,
      goal: 2,
      goal_milestone: 3,
      event: 4,
      questionnaire_setup: 5,
      coach_rules_setup: 6,
      program_setup: 7,
    };

    items.sort((a, b) => {
      const priorityDiff = (todoPriority[a.kind] ?? 99) - (todoPriority[b.kind] ?? 99);
      if (priorityDiff !== 0) return priorityDiff;
      return String(a.time || "99:99").localeCompare(String(b.time || "99:99"));
    });
    return items;
  }, [todayHabits, todayGoalsDue, todayGoalMilestonesDue, todayEvents, habitOverride, shouldShowProgramSetup, questionnaireFilled, todayWorkouts]);

  const [widgets, setWidgets] = useState([]);
  const [bigWidgets, setBigWidgets] = useState([]);
  const [widgetsLoading, setWidgetsLoading] = useState(false);
  const [savingLayout, setSavingLayout] = useState(false);

  const safeTiles = safeArray(widgets);
  const safeBig = safeArray(bigWidgets);

  const [lastSavedLayoutSig, setLastSavedLayoutSig] = useState(() => stableStringify({ widgets: [], big_widgets: [] }));

  const isLayoutDirty = useMemo(() => {
    return stableStringify({ widgets: safeTiles, big_widgets: safeBig }) !== lastSavedLayoutSig;
  }, [safeTiles, safeBig, lastSavedLayoutSig]);

  const [seriesByWidget, setSeriesByWidget] = useState({});
  const [seriesLoading, setSeriesLoading] = useState({});

  const [goalsProgressById, setGoalsProgressById] = useState({});
  const [habitsProgressById, setHabitsProgressById] = useState({});
  const [progressLoading, setProgressLoading] = useState({});

  const loadGoalProgress = useCallback(
    async (goalId) => {
      if (!token || !userId || !goalId) return;
      const key = `goal:${goalId}`;
      setProgressLoading((p) => ({ ...p, [key]: true }));
      try {
        const qs = new URLSearchParams();
        qs.set("user_id", "me");

        const out = await fetchJSON(`${BACKEND_URL}/api/goals/progress?${qs.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const list = Array.isArray(out?.goals) ? out.goals : Array.isArray(out) ? out : [];
        const map = {};
        for (const g of list) {
          if (!g?.goal_id) continue;
          map[g.goal_id] = (() => {
            const metricMet = g.metric_met;
            const metricActual = g.metric_actual;
            const hasMetric = metricMet === true || metricMet === false || metricActual != null;

            if (hasMetric) {
              const met = metricMet === true;
              return {
                title: g.title || "Goal",
                pct: met ? 100 : metricProgressPct(g.metric_rule, metricActual) ?? 0,
                completed: met ? 1 : 0,
                total: 1,
                metric_actual: metricActual ?? null,
                metric_met: g.metric_met ?? null,
                metric_rule: g.metric_rule ?? null,
                mode: "metric",
              };
            }

            return {
              title: g.title || "Goal",
              pct: Number(g.pct_complete || 0),
              completed: Number(g.completed_milestones || 0),
              total: Number(g.total_milestones || 0),
              metric_actual: null,
              metric_met: null,
              metric_rule: null,
              mode: "milestones",
            };
          })();
        }
        setGoalsProgressById(map);
      } catch (e) {
        console.warn("Failed to load goals progress:", e?.message || e);
      } finally {
        setProgressLoading((p) => ({ ...p, [key]: false }));
      }
    },
    [token, userId]
  );

  const loadHabitProgress = useCallback(
    async (habitId, days) => {
      if (!token || !userId || !habitId) return;
      const d = Number(days || 7);
      const key = `habit:${habitId}:${d}`;
      setProgressLoading((p) => ({ ...p, [key]: true }));

      try {
        const qs = new URLSearchParams();
        qs.set("user_id", "me");
        qs.set("days", String(d));

        const out = await fetchJSON(
          `${BACKEND_URL}/api/habits/${encodeURIComponent(habitId)}/progress?${qs.toString()}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const hasMetric = out?.metric_rule || out?.metric_actual != null || out?.metric_met != null;

        setHabitsProgressById((prev) => ({
          ...prev,
          [habitId]: {
            ...(prev[habitId] || {}),
            [d]: {
              name: out?.name || out?.title || "Habit",
              pct: hasMetric
                ? metricProgressPct(out?.metric_rule, out?.metric_actual) ?? 0
                : Number(out?.pct_complete || 0),
              completed: Number(out?.completed_count || out?.completed || 0),
              total: Number(out?.total_count || out?.total || 0),
              metric_rule: out?.metric_rule || null,
              metric_actual: out?.metric_actual ?? null,
              metric_met: out?.metric_met ?? null,
              mode: hasMetric ? "metric" : "checkbox",
            },
          },
        }));
      } catch (e) {
        console.warn("Failed to load habit progress:", e?.message || e);
      } finally {
        setProgressLoading((p) => ({ ...p, [key]: false }));
      }
    },
    [token, userId]
  );

  useEffect(() => {
    for (const w of safeTiles) {
      if (isGoalProgressTile(w) && w.goal_id) loadGoalProgress(w.goal_id);
      if (isHabitProgressTile(w) && w.habit_id) loadHabitProgress(w.habit_id, w.timeframe_days || 7);
    }
  }, [safeTiles, loadGoalProgress, loadHabitProgress]);

  const [tileEditOpen, setTileEditOpen] = useState({});
  const [progressEditMode, setProgressEditMode] = useState(false);
  const [bigGraphsEditMode, setBigGraphsEditMode] = useState(false);

  const toggleTileEdit = (id) => {
    if (!progressEditMode) return;
    setTileEditOpen((p) => ({ ...p, [id]: !p[id] }));
  };

  const [bigEditOpen, setBigEditOpen] = useState({});
  const toggleBigEdit = (id) => {
    if (!bigGraphsEditMode) return;
    setBigEditOpen((p) => ({ ...p, [id]: !p[id] }));
  };

  const loadLayout = useCallback(async () => {
    if (!token) return;
    setWidgetsLoading(true);
    try {
      const out = await fetchJSON(`${BACKEND_URL}/api/metrics/dashboard/widgets`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const t = safeArray(out.widgets);
      const b = safeArray(out.big_widgets);

      setWidgets(t);
      setBigWidgets(b);
      setLastSavedLayoutSig(stableStringify({ widgets: t, big_widgets: b }));
    } catch {
      // ignore
    } finally {
      setWidgetsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadLayout();
  }, [loadLayout]);

  const saveLayout = useCallback(
    async (overrideWidgets = null, overrideBigWidgets = null) => {
      if (!token) return;

      setSavingLayout(true);
      try {
        const widgetsToSave = Array.isArray(overrideWidgets) ? overrideWidgets : safeTiles;
        const bigToSave = Array.isArray(overrideBigWidgets) ? overrideBigWidgets : safeBig;

        const payload = {
          widgets: widgetsToSave,
          big_widgets: bigToSave,
        };

        const out = await fetchJSON(
          `${BACKEND_URL}/api/metrics/dashboard/widgets`,
          {
            method: "PUT",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
          }
        );

        const t = safeArray(out?.widgets);
        const b = safeArray(out?.big_widgets);

        setWidgets(t);
        setBigWidgets(b);
        setLastSavedLayoutSig(
          stableStringify({ widgets: t, big_widgets: b })
        );
      } catch (e) {
        console.warn("Save layout failed:", e?.message || e);
        showToast?.("Failed to save layout. Please try again.", "error");
      } finally {
        setSavingLayout(false);
      }
    },
    [token, safeTiles, safeBig, showToast]
  );

  const saveTileAndClose = useCallback(
    async (tileId) => {
      await saveLayout();
      setTileEditOpen((p) => ({ ...p, [tileId]: false }));
    },
    [saveLayout]
  );

  const saveBigAndClose = useCallback(
    async (graphId) => {
      await saveLayout();
      setBigEditOpen((p) => ({ ...p, [graphId]: false }));
    },
    [saveLayout]
  );

  const fetchSeries = useCallback(
    async (widget) => {
      if (!token || !widget?.id) return;
      if (isProgressTile(widget) || !widget.metric_key) return;
      setSeriesLoading((prev) => ({ ...prev, [widget.id]: true }));
      try {
        const qs = new URLSearchParams();
        qs.set("metric", widget.metric_key);
        qs.set("days", String(widget.timeframe_days || 30));
        qs.set("user_id", "me");

        const out = await fetchJSON(`${BACKEND_URL}/api/metrics/timeseries?${qs.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const points = safeArray(out.points).map((p) => ({
          date: p.date,
          value: p.value == null ? null : Number(p.value),
          label: compactDateLabel(p.date),
        }));

        setSeriesByWidget((prev) => ({ ...prev, [widget.id]: { points, aggregate: out.aggregate || {} } }));
      } catch {
        setSeriesByWidget((prev) => ({ ...prev, [widget.id]: { points: [], aggregate: {} } }));
      } finally {
        setSeriesLoading((prev) => ({ ...prev, [widget.id]: false }));
      }
    },
    [token]
  );

  useEffect(() => {
    const combined = [...safeTiles, ...safeBig];
    if (!combined.length) return;
    for (const w of combined) {
      if (!w?.metric_key || isProgressTile(w)) continue;
      fetchSeries(w);
    }
  }, [safeTiles, safeBig, fetchSeries]);

  const moveInArray = (arr, idx, dir) => {
    const next = arr.slice();
    const j = idx + dir;
    if (j < 0 || j >= next.length) return next;
    const tmp = next[idx];
    next[idx] = next[j];
    next[j] = tmp;
    return next;
  };

  const moveTile = (id, dir) => {
    setWidgets((prev) => {
      const a = safeArray(prev);
      const idx = a.findIndex((x) => x.id === id);
      if (idx < 0) return a;
      return moveInArray(a, idx, dir);
    });
    setPendingScroll({ type: "tile", id });
  };

  const moveBigGraph = (id, dir) => {
    setBigWidgets((prev) => {
      const a = safeArray(prev);
      const idx = a.findIndex((x) => x.id === id);
      if (idx < 0) return a;
      return moveInArray(a, idx, dir);
    });
    setPendingScroll({ type: "big", id });
  };

  const addTile = () => {
    const id = `w_${Math.random().toString(16).slice(2, 8)}`;
    const w = {
      id,
      metric_key: "wearables.steps",
      display: "sparkline",
      timeframe_days: 14,
      aggregation: "avg",
      label: "Steps",
    };

    setWidgets((prev) => [...safeArray(prev), w]);
    setTileEditOpen((p) => ({ ...p, [id]: true }));
    setPendingScroll({ type: "tile", id });
  };

  const addGoalProgressTile = () => {
    const id = `gp_${Math.random().toString(16).slice(2, 8)}`;
    const w = { id, kind: "goal_progress", goal_id: "", display: "ring", label: "Goal progress" };

    setWidgets((prev) => [...safeArray(prev), w]);
    setTileEditOpen((p) => ({ ...p, [id]: true }));
    setPendingScroll({ type: "tile", id });
  };

  const addHabitProgressTile = () => {
    const id = `hp_${Math.random().toString(16).slice(2, 8)}`;
    const w = {
      id,
      kind: "habit_progress",
      habit_id: "",
      display: "ring",
      timeframe_days: 7,
      label: "Habit progress",
    };

    setWidgets((prev) => [...safeArray(prev), w]);
    setTileEditOpen((p) => ({ ...p, [id]: true }));
    setPendingScroll({ type: "tile", id });
  };

  const [addTileMenuOpen, setAddTileMenuOpen] = useState(false);
  const [addTileKind, setAddTileKind] = useState("metric");

  const confirmAddSelectedTile = () => {
    if (addTileKind === "goal") addGoalProgressTile();
    else if (addTileKind === "habit") addHabitProgressTile();
    else addTile();
    setAddTileMenuOpen(false);
  };

  const removeTile = (id) => {
    setWidgets((prev) => {
      const next = safeArray(prev).filter((w) => w.id !== id);
      setTimeout(() => saveLayout(next, safeBig), 0);
      return next;
    });
    setTileEditOpen((p) => {
      const next = { ...p };
      delete next[id];
      return next;
    });
  };

  const updateTile = (id, patch) => {
    setWidgets((prev) =>
      safeArray(prev).map((w) => {
        if (w.id !== id) return w;

        const next = { ...w, ...patch };

        if (!isProgressTile(next)) {
          const lbl = (patch.label ?? w.label ?? "").trim() || metricLabel((patch.metric_key ?? w.metric_key) || "");
          return { ...next, label: lbl };
        }

        const lbl = (patch.label ?? next.label ?? "").trim();
        return { ...next, label: lbl };
      })
    );
  };

  const addBigGraph = () => {
    const id = `bg_${Math.random().toString(16).slice(2, 8)}`;
    const w = { id, metric_key: "wearables.steps", display: "line", timeframe_days: 30, aggregation: "avg", label: "Steps (30d)" };
    setBigWidgets((prev) => [...safeArray(prev), w]);
    setBigEditOpen((p) => ({ ...p, [id]: true }));
    setPendingScroll({ type: "big", id });
  };

  const removeBigGraph = (id) => {
    setBigWidgets((prev) => {
      const next = safeArray(prev).filter((w) => w.id !== id);
      setTimeout(() => saveLayout(safeTiles, next), 0);
      return next;
    });
    setBigEditOpen((p) => {
      const next = { ...p };
      delete next[id];
      return next;
    });
  };

  const updateBigGraph = (id, patch) => {
    setBigWidgets((prev) =>
      safeArray(prev).map((w) =>
        w.id === id
          ? { ...w, ...patch, label: (patch.label ?? w.label ?? "").trim() || metricLabel((patch.metric_key ?? w.metric_key) || "") }
          : w
      )
    );
  };

  const getTileMainNumber = (w, series) => {
    const agg = series?.aggregate || {};
    const val = agg?.[w.aggregation || "avg"];
    return val == null ? null : Number(val);
  };

  const renderTileBody = (w) => {
    if (isGoalProgressTile(w)) {
      if (!w.goal_id) return <div className="dash-muted">Select a goal…</div>;
      const key = `goal:${w.goal_id}`;
      if (progressLoading[key]) return <div className="dash-muted">Loading…</div>;
      const g = goalsProgressById[w.goal_id];
      if (!g) return <div className="dash-muted">No progress yet</div>;
      const pct = clamp(Number(g.pct || 0), 0, 100);

      if (w.display === "ring") {
        return (
          <div className="dash-tile-center" style={{ marginTop: 10 }}>
            <RingProgress value={pct} max={100} label={g.title} />
            <div className="dash-tile-sub">
              {g.mode === "metric"
                ? (g.metric_met === true ? "Met ✓" : g.metric_met === false ? "Not met ✕" : "—")
                : `${pct}% • ${g.completed}/${g.total} milestones`}
            </div>
          </div>
        );
      }

      const metricBar = g.mode === "metric" ? getMetricBarVisual(g.metric_rule, g.metric_actual) : null;

      return (
        <div style={{ width: "100%" }}>
          <div className="dash-tile-center" style={{ marginTop: 10, marginBottom: 10 }}>
            <div className="dash-tile-value-sm">{pct}%</div>
            {g.mode === "metric" && (
              <div className="dash-tile-sub" style={{ marginTop: 4 }}>
                {g.metric_met === true ? "Met ✓" : g.metric_met === false ? "Not met ✕" : ""}
              </div>
            )}
            <div className="dash-tile-sub">
              {g.mode === "metric"
                ? (g.metric_actual != null ? `Actual: ${fmtNum(g.metric_actual)}` : "Metric")
                : `${g.completed}/${g.total} milestones`}
            </div>
          </div>

          <div className="dash-pctbar dash-pctbar-metric" style={{ height: 8 }}>
            <div
              className="dash-pctbar-fill"
              style={metricBar || { width: `${pct}%` }}
            />
          </div>
        </div>
      );
    }

    if (isHabitProgressTile(w)) {
      if (!w.habit_id) return <div className="dash-muted">Select a habit…</div>;
      const days = Number(w.timeframe_days || 7);
      const key = `habit:${w.habit_id}:${days}`;
      if (progressLoading[key]) return <div className="dash-muted">Loading…</div>;
      const h = habitsProgressById?.[w.habit_id]?.[days];
      if (!h) return <div className="dash-muted">No progress yet</div>;
      const pct = clamp(Number(h.pct || 0), 0, 100);

      if (w.display === "ring") {
        return (
          <div className="dash-tile-center" style={{ marginTop: 10 }}>
            <RingProgress value={pct} max={100} label={h.name} />
            <div className="dash-tile-sub">
              {h.mode === "metric"
                ? (h.metric_met === true ? "Met ✓" : h.metric_met === false ? "Not met ✕" : "—")
                : `${pct}% • ${h.completed}/{h.total} (${days}d)`}
            </div>
          </div>
        );
      }

      const metricBar = h.mode === "metric" ? getMetricBarVisual(h.metric_rule, h.metric_actual) : null;

      return (
        <div style={{ width: "100%" }}>
          <div className="dash-tile-center" style={{ marginTop: 10, marginBottom: 10 }}>
            <div className="dash-tile-value-sm">{pct}%</div>

            {h.mode === "metric" && (
              <div className="dash-tile-sub" style={{ marginTop: 4 }}>
                {h.metric_met === true ? "Met ✓" : h.metric_met === false ? "Not met ✕" : ""}
              </div>
            )}

            <div className="dash-tile-sub">
              {h.mode === "metric"
                ? (h.metric_actual != null ? `Actual: ${fmtNum(h.metric_actual)}` : `Metric (${days}d)`)
                : `${h.completed}/${h.total} (${days}d)`}
            </div>
          </div>

          <div className="dash-pctbar dash-pctbar-metric">
            <div
              className="dash-pctbar-fill"
              style={metricBar || { width: `${pct}%` }}
            />
          </div>
        </div>
      );
    }

    const series = seriesByWidget[w.id];
    const loading = !!seriesLoading[w.id];
    if (loading) return <div className="dash-muted">Loading…</div>;

    const points = series?.points || [];
    const mainVal = getTileMainNumber(w, series);

    const isGraphDisplay = w.display === "sparkline" || w.display === "miniBar";
    const isBigNumberDisplay = w.display === "number" || w.display === "delta";
    const valueClass = isBigNumberDisplay ? "dash-tile-value" : "dash-tile-value-sm";

    const NumberHeader = (
      <div className="dash-tile-center" style={{ marginTop: 10, marginBottom: isGraphDisplay ? 8 : 10 }}>
        <div className={valueClass}>{mainVal == null ? "—" : fmtNum(mainVal)}</div>
        <div className="dash-tile-sub">
          {w.aggregation || "avg"} • {w.timeframe_days || 30}d
        </div>
      </div>
    );

    if (w.display === "ring") {
      const target = w.target != null ? Number(w.target) : w.metric_key === "app.calories_consumed" ? dailyTarget : 0;
      return (
        <div>
          <div className="dash-tile-center" style={{ marginTop: 10, marginBottom: 8 }}>
            <div className="dash-tile-value-sm">{mainVal == null ? "—" : fmtNum(mainVal)}</div>
            <div className="dash-tile-sub">{target ? `Target: ${fmtNum(target)}` : "Target: —"}</div>
          </div>
          <div className="dash-tile-center" style={{ marginTop: 6 }}>
            <RingProgress value={mainVal} max={target} label={w.label} />
          </div>
        </div>
      );
    }

    if (w.display === "delta") {
      const compareDays = Number(w.compare_days || 7);
      const d = calcDeltaFromPoints(points, compareDays);
      const dirClass = d.dir > 0 ? "dash-delta-up" : d.dir < 0 ? "dash-delta-down" : "dash-delta-flat";
      const arrow = d.dir > 0 ? "▲" : d.dir < 0 ? "▼" : "•";
      return (
        <div className="dash-tile-center" style={{ marginTop: 10 }}>
          <div className="dash-tile-value">{d.latest == null ? "—" : fmtNum(d.latest)}</div>
          <div className={`dash-delta ${dirClass}`}>
            {arrow} {d.pct == null ? "—" : `${Math.round(d.pct)}%`}{" "}
            <span className="dash-muted">({compareDays}d)</span>
          </div>
        </div>
      );
    }

    if (w.display === "number") return NumberHeader;

    if (!points.length) return <div>{NumberHeader}<div className="dash-muted">No data</div></div>;

    const h = 78;

    if (w.display === "sparkline") {
      return (
        <div>
          {NumberHeader}
          <div style={{ width: "100%", height: h }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={points}>
                <Line type="monotone" dataKey="value" dot={false} strokeWidth={2} />
                <Tooltip content={<TooltipContent />} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      );
    }

    if (w.display === "miniBar") {
      return (
        <div>
          {NumberHeader}
          <div style={{ width: "100%", height: h }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={points}>
                <Bar dataKey="value" />
                <Tooltip content={<TooltipContent />} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      );
    }

    return NumberHeader;
  };

  const renderBigChart = (w) => {
    const series = seriesByWidget[w.id];
    const loading = !!seriesLoading[w.id];
    if (loading) return <div className="dash-muted">Loading…</div>;

    const points = series?.points || [];
    if (!points.length) return <div className="dash-muted">No data</div>;

    const xAxis = <XAxis dataKey="label" tick={{ fontSize: 12 }} interval="preserveStartEnd" />;
    const yAxis = <YAxis tick={{ fontSize: 12 }} width={42} />;
    const H = 320;

    if (w.display === "line") {
      return (
        <div style={{ width: "100%", height: H }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={points}>
              <CartesianGrid strokeDasharray="3 3" />
              {xAxis}
              {yAxis}
              <Tooltip content={<TooltipContent />} />
              <Line type="monotone" dataKey="value" dot={false} strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      );
    }

    if (w.display === "area") {
      return (
        <div style={{ width: "100%", height: H }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={points}>
              <CartesianGrid strokeDasharray="3 3" />
              {xAxis}
              {yAxis}
              <Tooltip content={<TooltipContent />} />
              <Area type="monotone" dataKey="value" strokeWidth={2} fillOpacity={0.25} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      );
    }

    return (
      <div style={{ width: "100%", height: H }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={points}>
            <CartesianGrid strokeDasharray="3 3" />
            {xAxis}
            {yAxis}
            <Tooltip content={<TooltipContent />} />
            <Bar dataKey="value" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  };

  if (!data) return null;

  return (
    <div className="dash" data-testid="dashboard">
      <div className="today-overview-header">
        <div className="today-overview-title">
          <h2 className="heading-2">Today’s Overview</h2>
        </div>

        <div className="today-overview-water-input">
          <input
            className="dash-water-input"
            type="number"
            min={1}
            step={1}
            value={waterOz}
            onChange={(e) => setWaterOz(e.target.value)}
            aria-label="Water ounces"
            inputMode="numeric"
          />
          <span className="today-overview-water-display" aria-hidden="true">
            <span className="today-overview-water-display-value">{waterOz || 0}</span>
            <span className="today-overview-water-display-unit">oz</span>
          </span>
          <span className="today-overview-water-unit">oz</span>
        </div>

        <div className="today-overview-mobile-log-button">
          <button
            type="button"
            className="dash-btn dash-btn-primary"
            onClick={() => setShowMobileLogMenu(true)}
          >
            Log
          </button>
        </div>

        <div className="today-overview-date">
          <span>{selectedDate}</span>
          
        </div>

        <div className="today-overview-water-button">
          <button
            onClick={() => { setShowMobileLogMenu(false); logWater(); }}
            className="dash-btn dash-btn-primary dash-water-btn"
            type="button"
            disabled={waterSaving}
          >
            {waterSaving ? "Logging..." : "Log Water"}
          </button>
        </div>
        <div className="dash-actions dash-actions-mobile">
          <button onClick={openFoodLog} className="dash-btn dash-btn-primary" type="button">
            Log Food
          </button>

          <button onClick={openExerciseLog} className="dash-btn dash-btn-primary" type="button">
            Log Exercise
          </button>
        </div>

        <div className="today-overview-today-row">
          <button
            type="button"
            className="dash-btn dash-btn-ghost dash-btn-today"
            onClick={goToToday}
            disabled={selectedDate === todayISO}
          >
            Today
          </button>
        </div>

        <div className="dash-day-picker">
          <button type="button" className="dash-day-arrow" onClick={() => pageDays(-WINDOW)} title="Previous" aria-label="Previous">
            ◀
          </button>

          <div className="dash-day-strip">
            {daySquares.map((d) => {
              const active = d === selectedDate;
              return (
                <button
                  key={d}
                  type="button"
                  onClick={() => onPickDay(d)}
                  className={active ? "dash-day-square active" : "dash-day-square"}
                  title={d}
                >
                  <div className="dash-day-dow">{dayLabel(d)}</div>
                  <div className="dash-day-num">{dayNum(d)}</div>
                </button>
              );
            })}
          </div>

          <button type="button" className="dash-day-arrow" onClick={() => pageDays(WINDOW)} title="Next" aria-label="Next">
            ▶
          </button>
        </div>

        
      </div>

      {showMobileLogMenu ? (
        <div className="dash-mobile-log-menu-overlay" onClick={() => setShowMobileLogMenu(false)}>
          <div className="dash-mobile-log-menu" onClick={(e) => e.stopPropagation()}>
            <div className="dash-mobile-log-menu-title">Log for today</div>

      
            <button onClick={openFoodLog} className="dash-btn dash-btn-primary" type="button">
              Log Food
            </button>
            <button onClick={openExerciseLog} className="dash-btn dash-btn-primary" type="button">
              Log Exercise
            </button>
            <button type="button" className="dash-btn dash-btn-ghost" onClick={() => setShowMobileLogMenu(false)}>
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      <div className="dash-grid">
        <div className="dash-card dash-card-tasks">
          <div className="dash-card-head">
            <div className="dash-card-title">Things to do today</div>
            <div className="dash-card-meta">
              {sourceLabel ? <span className="dash-pill">⌚ {sourceLabel}</span> : <span className="dash-pill">✅ Daily checklist</span>}
              {questionnaireFilled === false && <span className="dash-pill">📝 Questionnaire</span>}
              {shouldShowProgramSetup && <span className="dash-pill">🧠 Create a Program</span>}
              {programsLoading && <span className="dash-pill">Checking programs…</span>}
              {todayHabitFeedLoading && <span className="dash-pill">Updating habits…</span>}
            </div>
          </div>

          <div className="dash-block">

            <MobileCarouselSection className="dash-list">
              {todoItems.map((t) => {
                if (t.kind === "questionnaire_setup") {
                  return (
                    <div key={`${t.kind}:${t.id}`} className="dash-task dash-task-mini">
                      <div className="dash-task-left" style={{ alignItems: "center" }}>
                        <div className={"dash-dot dash-dot-goal todo"} />
                        <div style={{ minWidth: 0 }}>
                          <div className="dash-task-title">{t.title}</div>
                          <div className="dash-task-sub">{t.subtitle}</div>
                        </div>
                      </div>
                      <div className="dash-task-right">
                        <button
                          type="button"
                          className="dash-btn dash-btn-primary"
                          onClick={() => {
                            onNavigate?.("profile");
                            setTimeout(() => {
                              window.dispatchEvent(new CustomEvent("profile:openEdit", { detail: { focus: "questionnaire" } }));
                            }, 150);
                          }}
                        >
                          Fill Out
                        </button>
                      </div>
                    </div>
                  );
                }

                if (t.kind === "coach_rules_setup") {
                  return (
                    <div key={`${t.kind}:${t.id}`} className="dash-task dash-task-mini">
                      <div className="dash-task-left" style={{ alignItems: "center" }}>
                        <div className={"dash-dot dash-dot-goal todo"} />
                        <div style={{ minWidth: 0 }}>
                          <div className="dash-task-title">{t.title}</div>
                          <div className="dash-task-sub">{t.subtitle}</div>
                        </div>
                      </div>
                      <div className="dash-task-right">
                        <button
                          type="button"
                          className="dash-btn dash-btn-primary"
                          onClick={() => setShowCoachRules(true)}
                        >
                          Coach Rules
                        </button>
                      </div>
                    </div>
                  );
                }

                if (t.kind === "program_setup" && shouldShowProgramSetup) {
                  return (
                    <div key={`${t.kind}:${t.id}`} className="dash-task dash-task-mini">
                      <div className="dash-task-left" style={{ alignItems: "center" }}>
                        <div className={"dash-dot dash-dot-goal todo"} />
                        <div style={{ minWidth: 0 }}>
                          <div className="dash-task-title">{t.title}</div>
                          <div className="dash-task-sub">{t.subtitle}</div>
                        </div>
                      </div>
                      <div className="dash-task-right">
                        <button type="button" className="dash-btn dash-btn-primary" onClick={openCreateProgramFromDashboard}>
                          Create Program
                        </button>
                      </div>
                    </div>
                  );
                }

                if (t.kind === "workout") {
                  const started = Boolean(t.started);
                  const finished = Boolean(t.finished || t.done);

                  const label = finished ? "Edit Workout" : started ? "Finish Workout" : "Start Workout";

                  return (
                    <div key={`${t.kind}:${t.id}`} className={`dash-task dash-task-mini ${finished ? "done" : ""}`}>
                      <div className="dash-task-left" style={{ alignItems: "center" }}>
                        <div className={`dash-dot dash-dot-goal ${finished ? "done" : "todo"}`} />
                        <div style={{ minWidth: 0 }}>
                          <div className="dash-task-title" style={{ textDecoration: finished ? "line-through" : "none", opacity: finished ? 0.8 : 1 }}>
                            {t.title}
                            {finished ? <span className="dash-pill" style={{ marginLeft: 8 }}>Completed</span> : null}
                          </div>
                          <div className="dash-task-sub">
                            {t.time ? `${t.time} • ` : ""}{t.subtitle}
                          </div>
                        </div>
                      </div>
                      <div className="dash-task-right">
                        <button
                          type="button"
                          className="dash-btn dash-btn-primary"
                          onClick={() => onStartWorkout?.(t.id)}
                        >
                          {label}
                        </button>
                      </div>
                    </div>
                  );
                }

                const isHabit = t.kind === "habit";
                const isCheckboxHabit = isHabit && t.habit_type === "checkbox";
                const isMetricHabit = isHabit && t.habit_type === "metric";
                const showCompleteState = !isMetricHabit && t.done;

                const dotClass =
                  t.kind === "event"
                    ? "dash-dot dash-dot-event"
                    : t.kind === "goal"
                    ? "dash-dot dash-dot-goal"
                    : isMetricHabit
                    ? "dash-dot dash-dot-metric"
                    : "dash-dot dash-dot-habit";

                return (
                  <div key={`${t.kind}:${t.id}`} className="dash-task dash-task-mini">
                    <div className="dash-task-left" style={{ alignItems: "center" }}>
                      <div className={`${dotClass} ${showCompleteState ? "done" : "todo"} ${isMetricHabit ? "metric" : ""}`} />
                      <div style={{ minWidth: 0 }}>
                        <div className="dash-task-title" style={{ textDecoration: showCompleteState ? "line-through" : "none" }}>
                          {t.title}
                        </div>
                        <div className="dash-task-sub">
                          {t.time ? `${t.time} • ` : ""}
                          {t.subtitle}
                          {isMetricHabit && t.metric_rule ? (() => {
                            const barStyle = getMetricBarVisual(t.metric_rule, t.metric_actual);
                            if (!barStyle) return null;

                            const actual = t.metric_actual;
                            const target = t.metric_rule?.value;
                            const helperText = metricProgressText(t.metric_rule, actual);

                            return (
                              <div style={{ marginTop: 8 }}>
                                <div className="dash-pctbar dash-pctbar-metric">
                                  <div
                                    className="dash-pctbar-fill"
                                    style={barStyle}
                                  />
                                </div>

                                <div className="dash-muted" style={{ marginTop: 6 }}>
                                  {actual != null
                                    ? `Actual: ${fmtNum(actual)} • Target: ${opLabel(t.metric_rule?.operator)} ${fmtNum(target)}`
                                    : `Target: ${opLabel(t.metric_rule?.operator)} ${fmtNum(target)}`}
                                </div>

                                {helperText ? (
                                  <div className="dash-muted" style={{ marginTop: 2 }}>
                                    {helperText}
                                  </div>
                                ) : null}
                              </div>
                            );
                          })() : null}
                        </div>
                      </div>
                    </div>

                    <div className="dash-task-right">
                      {isCheckboxHabit ? (
                        <button
                          type="button"
                          className={`dash-btn ${t.done ? "dash-btn-success" : ""}`}
                          disabled={savingHabitId === t.id}
                          onClick={() => toggleHabitComplete(t.id, !t.done)}
                        >
                          {savingHabitId === t.id ? "…" : t.done ? "Undo" : "Complete"}
                        </button>
                      ) : t.kind === "goal" || t.kind === "goal_milestone" || t.kind === "event" ? (
                        <button
                          type="button"
                          className="dash-btn"
                          onClick={() => ((t.kind === "goal" || t.kind === "goal_milestone") ? onNavigate("goals") : onNavigate("calendar"))}
                        >
                          View
                        </button>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </MobileCarouselSection>
          </div>
        </div>

        <div className="dash-card dash-card-progress dash-progress-card">
          <div className="dash-card-head dash-card-head-stacked">
            <div className="dash-section-titlebar">
              <div className="dash-card-title">Progress</div>

              <div className="dash-section-actions">
                <button
                  type="button"
                  className="dash-btn done-edit-button"
                  onClick={() => {
                    setProgressEditMode((v) => {
                      const next = !v;
                      if (!next) setTileEditOpen({});
                      return next;
                    });
                  }}
                >
                  {progressEditMode ? "Done" : "Edit"}
                </button>

                <button
                  type="button"
                  className="ct-icon-btn"
                  onClick={() => setAddTileMenuOpen(true)}
                  title="Add tile"
                  aria-label="Add tile"
                >
                  +
                </button>
              </div>
            </div>

            <div className="dash-section-subtitle-row">
              <div className="body-small dash-mobile-hide-subheader" style={{ color: "var(--text-secondary)" }}>
                Tiles show metrics + trends.
              </div>
              {widgetsLoading && <span className="dash-pill">Loading…</span>}
            </div>
          </div>

          {addTileMenuOpen && (
            <div
              className="dash-modal-overlay"
              style={{
                position: "fixed",
                inset: 0,
                background: "var(--bg-modal)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 16,
                zIndex: 1000,
              }}
              role="dialog"
              aria-modal="true"
              aria-label="Add progress tile"
              onClick={() => setAddTileMenuOpen(false)}
            >
              <div
                style={{
                  width: "min(520px, 100%)",
                  background: "var(--bg-modal)",
                  border: "1px solid var(--border-light)",
                  borderRadius: 14,
                  boxShadow: "0 12px 40px rgba(0,0,0,0.22)",
                  padding: 16,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                  <div style={{ fontWeight: 700, fontSize: 16 }}>Add a progress tile</div>
                  <button
                    type="button"
                    className="ct-icon-btn"
                    onClick={() => setAddTileMenuOpen(false)}
                    title="Close"
                    aria-label="Close"
                  >
                    ✕
                  </button>
                </div>

                <div style={{ marginTop: 12 }}>
                  <div className="dash-muted" style={{ marginBottom: 8 }}>
                    Choose a tile type (you can switch any time):
                  </div>

                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <button
                      type="button"
                      className={addTileKind === "metric" ? "dash-btn dash-btn-primary" : "dash-btn"}
                      onClick={() => setAddTileKind("metric")}
                    >
                      Metric tile
                    </button>
                    <button
                      type="button"
                      className={addTileKind === "goal" ? "dash-btn dash-btn-primary" : "dash-btn"}
                      onClick={() => setAddTileKind("goal")}
                    >
                      Goal progress
                    </button>
                    <button
                      type="button"
                      className={addTileKind === "habit" ? "dash-btn dash-btn-primary" : "dash-btn"}
                      onClick={() => setAddTileKind("habit")}
                    >
                      Habit progress
                    </button>
                  </div>

                  <div className="dash-muted" style={{ marginTop: 10 }}>
                    After adding, the tile will open in edit mode so you can pick the metric / goal / habit.
                  </div>
                </div>

                <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 16 }}>
                  <button type="button" className="dash-btn" onClick={() => setAddTileMenuOpen(false)}>
                    Cancel
                  </button>
                  <button type="button" className="dash-btn dash-btn-primary" onClick={confirmAddSelectedTile}>
                    Add
                  </button>
                </div>
              </div>
            </div>
          )}

          <div style={{ marginTop: 12 }}><MobileCarouselSection className="dash-tiles" slideClassName="dash-mobile-progress-slide">
            {safeTiles.map((w) => (
              <div
                key={w.id}
                className="dash-tile"
                ref={(el) => {
                  if (el) tileRefs.current[w.id] = el;
                }}
              >
                <div className="dash-tile-head">
                  <div className="dash-tile-title">{tileTitle(w)}</div>
                  <div className="dash-tile-icons">
                    {progressEditMode ? (
                      tileEditOpen[w.id] ? (
                        <>
                          <button type="button" className="ct-icon-btn" title="Move up" onClick={() => moveTile(w.id, -1)}>⬆️</button>
                          <button type="button" className="ct-icon-btn" title="Move down" onClick={() => moveTile(w.id, +1)}>⬇️</button>
                        </>
                      ) : (
                        <>
                          <button type="button" className="ct-icon-btn" title="Edit" onClick={() => toggleTileEdit(w.id)}>🔧</button>
                          <button type="button" className="ct-icon-btn" title="Remove" onClick={() => removeTile(w.id)}>🗑️</button>
                        </>
                      )
                    ) : null}
                  </div>
                </div>

                {tileEditOpen[w.id] && (
                  <div style={{ marginTop: 10 }}>
                    <div className="dash-progress-builder ct-editor">
                      {isProgressTile(w) ? (
                        <>
                          {isGoalProgressTile(w) && (
                            <div className="dash-pb-group">
                              <div className="dash-pb-label">Goal</div>
                              <select
                                value={w.goal_id || ""}
                                onChange={(e) => {
                                  const id = e.target.value;
                                  const g = safeArray(allGoals).find((x) => String(x.goal_id) === String(id));
                                  updateTile(w.id, { goal_id: id, label: g?.title || "Goal progress" });
                                  if (id) loadGoalProgress(id);
                                }}
                                className="form-input"
                              >
                                <option value="">{allGoalsLoading ? "Loading goals…" : (safeArray(allGoals).length ? "Select a goal…" : "No active goals found")}</option>
                                {safeArray(allGoals).map((g) => (
                                  <option key={g.goal_id} value={g.goal_id}>
                                    {g.title}{g.metric_rule ? " (metric)" : ""}
                                  </option>
                                ))}
                              </select>
                            </div>
                          )}

                          {isHabitProgressTile(w) && (
                            <div className="dash-pb-group">
                              <div className="dash-pb-label">Habit</div>
                              <select
                                value={w.habit_id || ""}
                                onChange={(e) => {
                                  const id = e.target.value;
                                  const h = safeArray(allHabits).find((x) => String(x.habit_id) === String(id));
                                  updateTile(w.id, { habit_id: id, label: h?.name || h?.title || "Habit progress" });
                                  if (id) loadHabitProgress(id, w.timeframe_days || 7);
                                }}
                                className="form-input"
                              >
                                <option value="">{allHabitsLoading ? "Loading habits…" : "Select a habit…"}</option>
                                {safeArray(allHabits).map((h) => (
                                  <option key={h.habit_id} value={h.habit_id}>
                                    {(h.name || h.title || "Habit") + (h?.habit_type === "metric" && h?.metric_rule ? ` • ${h.metric_rule.domain}.${h.metric_rule.field} ${opLabel(h.metric_rule.operator)} ${h.metric_rule.value}` : "")}
                                  </option>
                                ))}
                              </select>
                            </div>
                          )}

                          <div className="dash-pb-group">
                            <div className="dash-pb-label">Display</div>
                            <select
                              value={w.display || "ring"}
                              onChange={(e) => updateTile(w.id, { display: e.target.value })}
                              className="form-input"
                            >
                              {PROGRESS_DISPLAY_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </select>
                          </div>

                          {isHabitProgressTile(w) && (
                            <div className="dash-pb-group">
                              <div className="dash-pb-label">Timeframe</div>
                              <select
                                value={w.timeframe_days || 7}
                                onChange={(e) => {
                                  const days = Number(e.target.value);
                                  updateTile(w.id, { timeframe_days: days });
                                  if (w.habit_id) loadHabitProgress(w.habit_id, days);
                                }}
                                className="form-input"
                              >
                                {TIMEFRAME_OPTIONS.map((opt) => (
                                  <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          <div className="dash-pb-group">
                            <div className="dash-pb-label">Metric</div>
                            <select
                              value={w.metric_key}
                              onChange={(e) => updateTile(w.id, { metric_key: e.target.value, label: metricLabel(e.target.value) })}
                              className="form-input"
                            >
                              {METRIC_OPTIONS.map((g) => (
                                <optgroup key={g.group} label={g.group}>
                                  {g.items.map((it) => (
                                    <option key={it.value} value={it.value}>
                                      {it.label}
                                    </option>
                                  ))}
                                </optgroup>
                              ))}
                            </select>
                          </div>

                          <div className="dash-pb-group">
                            <div className="dash-pb-label">Display</div>
                            <select value={w.display} onChange={(e) => updateTile(w.id, { display: e.target.value })} className="form-input">
                              {TILE_DISPLAY_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </select>
                          </div>

                          {w.display === "ring" && (
                            <div className="dash-pb-group">
                              <div className="dash-pb-label">Target (optional)</div>
                              <input
                                type="number"
                                value={w.target ?? ""}
                                onChange={(e) => updateTile(w.id, { target: e.target.value === "" ? null : Number(e.target.value) })}
                                className="form-input"
                              />
                            </div>
                          )}

                          {w.display === "delta" && (
                            <div className="dash-pb-group">
                              <div className="dash-pb-label">Compare</div>
                              <select
                                value={w.compare_days || 7}
                                onChange={(e) => updateTile(w.id, { compare_days: Number(e.target.value) })}
                                className="form-input"
                              >
                                {COMPARE_DAYS_OPTIONS.map((opt) => (
                                  <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                          )}

                          <div className="dash-pb-group">
                            <div className="dash-pb-label">Timeframe</div>
                            <select
                              value={w.timeframe_days}
                              onChange={(e) => updateTile(w.id, { timeframe_days: Number(e.target.value) })}
                              className="form-input"
                            >
                              {TIMEFRAME_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div className="dash-pb-group">
                            <div className="dash-pb-label">Aggregation</div>
                            <select value={w.aggregation} onChange={(e) => updateTile(w.id, { aggregation: e.target.value })} className="form-input">
                              {AGG_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </>
                      )}

                      <div className="ct-editor-actions">
                        <button
                          type="button"
                          className="dash-btn"
                          onClick={() => toggleTileEdit(w.id)}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="dash-btn dash-btn-primary"
                          onClick={() => saveTileAndClose(w.id)}
                          disabled={savingLayout || !isLayoutDirty}
                          title={isLayoutDirty ? "Save layout" : "No changes"}
                        >
                          {savingLayout ? "Saving…" : "Save"}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <div style={{ marginTop: 10 }} className="dash-tile-body">
                  {renderTileBody(w)}
                </div>
              </div>
            ))}
          </MobileCarouselSection></div>
        </div>
      </div>

      {coachProgramSummary && (
        <div className="dash-card" style={{ marginTop: 14 }}>
          <div className="dash-card-head">
            <div>
              <div className="dash-card-title">Coach Program Progress</div>
            </div>
            {coachProgramSummaryLoading && <span className="dash-pill">Loading…</span>}
          </div>

          <div style={{ marginTop: 12 }}>
            <MobileCarouselSection className="dash-coach-grid" slideClassName="dash-mobile-coach-slide">
              {[
              ["Start weight", coachProgramSummary.start_weight],
              ["Current weight", coachProgramSummary.current_weight],
              ["Target weight", coachProgramSummary.target_weight],
              ["Days completed", coachProgramSummary.days_completed],
              ["Workouts completed", coachProgramSummary.workouts_completed],
              ["Meals completed", coachProgramSummary.meals_completed],
              ["Avg calories", coachProgramSummary.avg_calories],
              ["Avg protein", coachProgramSummary.avg_protein],
              ["Completion %", coachProgramSummary.completion_pct],
            ].map(([label, value]) => (
              <div
                key={label}
                className="dash-tile"
                style={{ minHeight: 110, display: "flex", flexDirection: "column", justifyContent: "center" }}
              >
                <div className="dash-tile-title">{label}</div>
                <div className="dash-tile-value" style={{ marginTop: 8 }}>
                  {value == null ? "—" : fmtNum(value)}
                </div>
              </div>
            ))}
            </MobileCarouselSection>
          </div>
        </div>
      )}

      <div className="dash-card" style={{ marginTop: 14 }}>
        <div className="dash-card-head dash-card-head-stacked">
          <div className="dash-section-titlebar">
            <div className="dash-card-title">Graphs</div>

            <div className="dash-section-actions">
              <button
                type="button"
                className="dash-btn done-edit-button"
                onClick={() => {
                  setBigGraphsEditMode((v) => {
                    const next = !v;
                    if (!next) setBigEditOpen({});
                    return next;
                  });
                }}
              >
                {bigGraphsEditMode ? "Done" : "Edit"}
              </button>

              <button
                type="button"
                className="ct-icon-btn"
                onClick={addBigGraph}
                title="Add graph"
                aria-label="Add graph"
              >
                +
              </button>
            </div>
          </div>

          <div className="dash-section-subtitle-row">
            <div className="body-small dash-mobile-hide-subheader" style={{ color: "var(--text-secondary)" }}>
              Bigger charts for deeper trends.
            </div>
          </div>
        </div>

        <div style={{ marginTop: 12 }}><MobileCarouselSection className="dash-biggraphs-grid" slideClassName="dash-mobile-graph-slide">
          {safeBig.map((w) => (
            <div
              key={w.id}
              ref={(el) => {
                if (el) bigRefs.current[w.id] = el;
              }}
              className="ct-biggraph-card"
              style={{ padding: 16 }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                <div style={{ fontWeight: 600, fontSize: 16 }}>{tileTitle(w)}</div>

                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  {bigGraphsEditMode ? (
                    bigEditOpen[w.id] ? (
                      <>
                        <button type="button" className="ct-icon-btn" title="Move up" onClick={() => moveBigGraph(w.id, -1)}>⬆️</button>
                        <button type="button" className="ct-icon-btn" title="Move down" onClick={() => moveBigGraph(w.id, +1)}>⬇️</button>
                      </>
                    ) : (
                      <>
                        <button type="button" className="ct-icon-btn" title="Edit" onClick={() => toggleBigEdit(w.id)}>🔧</button>
                        <button type="button" className="ct-icon-btn" title="Remove" onClick={() => removeBigGraph(w.id)}>🗑️</button>
                      </>
                    )
                  ) : null}
                </div>
              </div>

              {bigEditOpen[w.id] && (
                <div style={{ marginTop: 12 }}>
                  <div className="dash-progress-builder ct-editor">
                    <div className="dash-pb-group">
                      <div className="dash-pb-label">Metric</div>
                      <select value={w.metric_key} onChange={(e) => updateBigGraph(w.id, { metric_key: e.target.value, label: metricLabel(e.target.value) })} className="form-input">
                        {METRIC_OPTIONS.map((g) => (
                          <optgroup key={g.group} label={g.group}>
                            {g.items.map((it) => (
                              <option key={it.value} value={it.value}>
                                {it.label}
                              </option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                    </div>

                    <div className="dash-pb-group">
                      <div className="dash-pb-label">Display</div>
                      <select value={w.display} onChange={(e) => updateBigGraph(w.id, { display: e.target.value })} className="form-input">
                        {BIG_DISPLAY_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="dash-pb-group">
                      <div className="dash-pb-label">Timeframe</div>
                      <select value={w.timeframe_days} onChange={(e) => updateBigGraph(w.id, { timeframe_days: Number(e.target.value) })} className="form-input">
                        {TIMEFRAME_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="dash-pb-group">
                      <div className="dash-pb-label">Aggregation</div>
                      <select value={w.aggregation || "avg"} onChange={(e) => updateBigGraph(w.id, { aggregation: e.target.value })} className="form-input">
                        {AGG_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="ct-editor-actions">
                      <button
                        type="button"
                        className="dash-btn"
                        onClick={() => toggleBigEdit(w.id)}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="dash-btn dash-btn-primary"
                        onClick={() => saveBigAndClose(w.id)}
                        disabled={savingLayout || !isLayoutDirty}
                        title={isLayoutDirty ? "Save layout" : "No changes"}
                      >
                        {savingLayout ? "Saving…" : "Save"}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div style={{ marginTop: 12 }}>{renderBigChart(w)}</div>
            </div>
          ))}
        </MobileCarouselSection></div>
      </div>

      <CoachRulesModal
        isOpen={showCoachRules}
        onClose={() => setShowCoachRules(false)}
        token={token}
        userId={userId || ""}
        titleName="Me"
        readOnly={false}
        onSaved={() => {
          loadSetupStatus();
        }}
        showToast
      />
    </div>
  );
}

export default Dashboard;